import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { useForm, FormProvider, useWatch, useFieldArray } from "react-hook-form";
import {
  DocumentService,
  EntityType,
  PaymentService,
  TenantService,
  PaidFlowTypesEnum,
  PayeeAccountTypesEnum,
  PaymentDueAccountVM,
  OrganizationService,
} from "@propertelligent/client-api";
import apiHelper from "../../utils/apiHelper";
import { NotificationType, PubSub, TopicsEnum } from "../../utils/pubsub";
import DateHelper from "../../utils/DateHelper";
import { UserLoginData } from "../../components/common/charts/Enums";
import InputField from "../form-fields/InputField";
import DescriptionTextarea from "../form-fields/DescriptionTextarea";
import CurrencyInput from "../form-fields/CurrencyInput";
import FixedFooter from "../listings/add-listing-footer/fixed-footer";
import SelectField from "../form-fields/SelectField";
import LeftArrowTopHeader from "../form-fields/LeftArrowTopHeader";
import { Accordion, AccordionSection } from "../card/accordion";
import SimpleOutlineButton from "../buttons/Simple-Outline-button";
import DocumentUploader from "../CustomUploader/DocumentUploader";
import SvgRoundIconButton from "../buttons/Svg-Round-Icon-button";
import { DeleteRedIcon } from "../../assets/svgs/DeleteRedIcon";
import RightSideModal from "../Modal/right-side-modal";
import Card from "../card";
import DynamicTabs from "../tabs/DynamicTabs";
import DuesORTransaction from "./DuesORTransaction";
import './payment.css';
import './AddPaymentModal.css';
import SimpleSpinner from "../../components/loader";
import EmptyState from "../emptystate";
import { GetImages } from "../../utils/GetImages";
import { utcDateFormat } from "../../utils/helper";
import ReceivableDetailCard from "../listings/listing-cards/receivable-detail";
import SimpleButton from "../buttons/Simple-button";
import PaymentDashboard from "./PaymentDashboard";
import { t } from "i18next";
import { tr } from "date-fns/locale";
import {
  fetchAllPaymentReferenceDocumentIds,
  fetchFirstPaymentReferenceDocument,
  uploadPaymentReferenceDocument,
} from "./paymentDocumentHelpers";
import { buildInvoiceItemsFullyQualifiedFilter, filterDuesByPaidFlow, getPaymentListingPath, interpretHeaderResponse, scopeInvoiceItemsForEdit } from "./paymentListHelpers";

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

interface PaymentItem {
  id?: number;
  PaymentType: string;
  Amount: string;
  DueDate: string;
  GracePeriod: string;
  Description: string;
}

interface FormValues {
  tenantId?: string;
  documentfile?: File | null;
  AccountNumber?: string;
  InvoiceNo?: string;
  payments: PaymentItem[];
}

interface AccordionState {
  basicDetailsOpen: boolean;
  paymentInfoOpen: boolean;
}

interface ModalState {
  viewAll: boolean;
}

interface ApiData {
  tenants: any[];
  paymentTypes: any[];
  dues: PaymentDueAccountVM[];
  transactions: any[];
  selectedReceivableDetail: PaymentDueAccountVM | null;
}

interface EditReceivableProps {
  fetchData?: () => void;
  isEntityProp?: boolean;
  entityIdProp?: number;
  entityTypeIdProp?: EntityType;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const INITIAL_ACCORDION_STATE: AccordionState = {
  basicDetailsOpen: true,
  paymentInfoOpen: false,
};

const INITIAL_MODAL_STATE: ModalState = {
  viewAll: false,
};

const DEFAULT_PAYMENT_ITEM: PaymentItem = {
  id: undefined,
  PaymentType: "",
  Amount: "",
  DueDate: "",
  GracePeriod: "",
  Description: "",
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const EditReceivable = ({ fetchData, isEntityProp, entityIdProp, entityTypeIdProp }: EditReceivableProps) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const urlTenantId = searchParams.get("tenantId");
  const urlPaymentType = searchParams.get("paymentType");
  const [payeeAccountId, setPayeeAccountId] = useState<number>(0);

  const organizationId = Number(sessionStorage.getItem(UserLoginData.organization_id));

  const [resetCounter, setResetCounter] = useState(0);
  const [modals, setModals] = useState<ModalState>(INITIAL_MODAL_STATE);
  const [data, setData] = useState<ApiData>({
    tenants: [],
    paymentTypes: [],
    dues: [],
    transactions: [],
    selectedReceivableDetail: null,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingHeader, setIsLoadingHeader] = useState(true);
  const [isDuesLoading, setIsDuesLoading] = useState(false);
  const [isTransactionsLoading, setIsTransactionsLoading] = useState(false);
  const [accordionState, setAccordionState] = useState<AccordionState>(INITIAL_ACCORDION_STATE);
  const [selectedPayment, setSelectedPayment] = useState<PaymentDueAccountVM | null>(null);
  const [originalPaymentIds, setOriginalPaymentIds] = useState<number[]>([]);
  const [originalDocumentId, setOriginalDocumentId] = useState<number | null>(null);
  const [loadedDocumentFile, setLoadedDocumentFile] = useState<File | null>(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);

  const isEntity = isEntityProp ?? (searchParams.get('isEntity') === 'true');
  const entityId = entityIdProp ?? (Number(searchParams.get('entityId')) || undefined);
  const entityTypeId = entityTypeIdProp ?? (Number(searchParams.get('entityTypeId')) || undefined);

  const listingPath = useMemo(
    () =>
      getPaymentListingPath({
        isEntity,
        entityId,
        entityTypeId,
        paidFlowTypeId: PaidFlowTypesEnum.Receiveable,
      }),
    [isEntity, entityId, entityTypeId],
  );

  // Form Setup
  const methods = useForm<FormValues>({
    defaultValues: {
      tenantId: urlTenantId || "",
      documentfile: null,
      AccountNumber: "",
      InvoiceNo: "",
      payments: [DEFAULT_PAYMENT_ITEM],
    },
    mode: "onBlur",
  });

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { isDirty, isValid },
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "payments",
  });

  const tenantId = useWatch({ control, name: "tenantId" });

  // ============================================================================
  // API CALLS
  // ============================================================================

  const fetchTenants = async () => {
    try {
      const res = await apiHelper(TenantService.getApiTenantOrganizationlookup, { showNotification: false }, organizationId);
      setData(prev => ({ ...prev, tenants: res?.data?.success || [] }));

    } catch (error) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to fetch tenants",
        type: NotificationType.Failure,
      });
    }
  };

  const fetchPaymentTypes = async () => {
    try {
      const res = await apiHelper(
        PaymentService.getApiPayeeAccountPaymentTypes,
        { showNotification: false },
        PayeeAccountTypesEnum.Tenant
      );
      setData(prev => ({ ...prev, paymentTypes: res?.data?.success || [] }));

    } catch (error) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to fetch payment types",
        type: NotificationType.Failure,
      });
    }
  };

  const fetchTenantDues = useCallback(async (tid: string) => {
    if (!tid) return;
    setIsDuesLoading(true);
    try {
      const res = await apiHelper(
        PaymentService.getApiInvoicePayeeAccountTenantList,
        { showNotification: false },
        Number(tid)
      );

      const dues = res?.data?.success || [];
      setData(prev => ({ ...prev, dues }));

      // Always re-bind the left-rail selection to this tenant's dues. An empty
      // list must clear selection and drop the full-page loader — otherwise
      // isLoading stays true forever (handlePaymentSelect never runs).
      if (dues.length > 0) {
        handlePaymentSelect(dues[0]);
      } else {
        setSelectedPayment(null);
        setIsLoading(false);
      }
    } catch (error) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to fetch dues",
        type: NotificationType.Failure,
      });
    } finally {
      setIsDuesLoading(false);
      setIsLoadingHeader(false);
    }
  }, []); // handlePaymentSelect is stable enough for auto-select; avoid stale selectedPayment gate


  const fetchTransactions = useCallback(async () => {
    if (!tenantId) return;
    setIsTransactionsLoading(true);
    try {
      const res = await apiHelper(
        OrganizationService.getApiOrganizationTransactionTenantlist,
        { showNotification: false },
        Number(tenantId),
        5,
        1,
        undefined,
        undefined,
        undefined,
        undefined,
        "PaymentDate desc"
      );
      setData(prev => ({ ...prev, transactions: res?.data?.success || [] }));
    } catch (error) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to fetch transactions",
        type: NotificationType.Failure,
      });
    } finally {
      setIsTransactionsLoading(false);
    }
  }, [tenantId]);

  const loadInvoiceItems = useCallback(async (
    referenceNumber: string,
    tenantId: string,
    paymentId?: number,
    paymentDueAccountIds?: number[] | null,
  ) => {
    try {
      const tenantIdNum = Number(tenantId);
      const res = await apiHelper(
        PaymentService.getApiPaymentDueAccountInvoiceitems,
        { showNotification: false },
        referenceNumber,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        buildInvoiceItemsFullyQualifiedFilter({
          paidFlowTypeId: PaidFlowTypesEnum.Receiveable,
          entityId: tenantIdNum,
        }),
      );
      const items = scopeInvoiceItemsForEdit(
        filterDuesByPaidFlow(
          res?.data?.success || [],
          PaidFlowTypesEnum.Receiveable,
          { keepUnknown: true },
        ),
        {
          paymentDueAccountIds,
          entityId: tenantIdNum,
        },
      );
      if (items.length === 0) return null;

      const firstItem = items[0];
      const loadedDoc = await fetchFirstPaymentReferenceDocument(
        items.map((item: any) => item.id),
        paymentId,
      );
      if (loadedDoc) {
        setOriginalDocumentId(loadedDoc.documentId);
      } else {
        setOriginalDocumentId(null);
      }
      setLoadedDocumentFile(loadedDoc?.file ?? null);

      const formData: FormValues = {
        tenantId,
        documentfile: loadedDoc?.file ?? null,
        AccountNumber: firstItem.accountNumber || "",
        InvoiceNo: firstItem.referenceNumber || "",
        payments: items.map((item: any) => ({
          id: item.id,
          PaymentType: String(item.paymentTypeId),
          Amount: String(item.amount),
          DueDate: DateHelper.convertUTCtoDateTime2(item.dueDate),
          GracePeriod: String(item.gracePeriodInDays || 0),
          Description: item.description || "",
        })),
      };

      return formData;
    } catch (error) {
      console.error("Failed to load invoice items:", error);
      return null;
    }
  }, []);

  const fetchReceivableDetail = async (tid: string) => {
    try {
      const res = await apiHelper(
        PaymentService.getApiInvoicePayeeAccountTenantHeader,
        { showNotification: false },
        Number(tid)
      );

      // 404 = tenant has no receivable header (expected when no open dues).
      // Real failures throw so we can clear the previous tenant's card.
      const detail = interpretHeaderResponse(res);
      setData(prev => ({ ...prev, selectedReceivableDetail: detail }));
      setPayeeAccountId(detail?.payeeAccountId || 0);
    } catch (error) {
      console.error("Failed to fetch receivable detail", error);
      setData(prev => ({ ...prev, selectedReceivableDetail: null }));
      setPayeeAccountId(0);
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to fetch receivable detail",
        type: NotificationType.Failure,
      });
    }
  };

  // ============================================================================
  // HANDLERS & EFFECTS
  // ============================================================================

  const toggleModal = (modalName: keyof ModalState) => {
    setModals(prev => ({ ...prev, [modalName]: !prev[modalName] }));
  };

  const toggleAccordion = (section: keyof AccordionState) => {
    setAccordionState(prev => ({
      basicDetailsOpen: section === 'basicDetailsOpen',
      paymentInfoOpen: section === 'paymentInfoOpen',
    }));
  };

  const handlePaymentSelect = async (payment: PaymentDueAccountVM) => {
    setSelectedPayment(payment);
    if (!payment.referenceNumber) return;

    setIsDetailLoading(true);
    try {
      // Pass payment.id to loadInvoiceItems to fetch the document
      const formData = await loadInvoiceItems(
        payment.referenceNumber,
        tenantId,
        payment.id,
        payment.paymentDueAccountIds,
      );
      if (formData) {
        const loadedIds = formData.payments
          .map(p => p.id)
          .filter((id): id is number => !!id && id > 0);
        setOriginalPaymentIds(loadedIds);
        reset(formData);
        setAccordionState({ basicDetailsOpen: true, paymentInfoOpen: true });
      }
    } catch (error) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to load payment details",
        type: NotificationType.Failure,
      });
    } finally {
      setIsLoading(false);
      setIsDetailLoading(false);
    }
  };

  const openPaymentInfo = () => {
    setAccordionState({ basicDetailsOpen: false, paymentInfoOpen: true });
  };

  const createPaymentObject = (formData: FormValues, item: PaymentItem, entityId: number) => ({
    amount: Number(item.Amount),
    description: item.Description,
    dueDate: utcDateFormat(item.DueDate),
    entityId,
    entityTypeId: EntityType.Tenant,
    accountNumber: formData.AccountNumber,
    payeeAccountId: payeeAccountId,
    gracePeriodInDays: Number(item.GracePeriod) || 0,
    referenceNumber: formData.InvoiceNo,
    paymentTypeId: Number(item.PaymentType) || 0,
    paidFlowTypeId: PaidFlowTypesEnum.Receiveable,
    organizationId,
  });

  const uploadDocument = async (paymentId: number, file: File) => {
    await uploadPaymentReferenceDocument(paymentId, file);
  };

  const handlePaymentUpdate = async (formData: FormValues) => {
    setIsLoading(true);
    try {
      const entityId = Number(formData.tenantId);

      const currentIds = new Set(
        formData.payments
          .map(p => p.id)
          .filter((id): id is number => !!id && id > 0)
      );
      const idsToDelete = originalPaymentIds.filter(id => !currentIds.has(id));

      const documentUnchanged =
        !!formData.documentfile && formData.documentfile === loadedDocumentFile;
      // Collected before any upload so the new copies are never in this list.
      const previousDocumentIds =
        originalDocumentId && !documentUnchanged
          ? await fetchAllPaymentReferenceDocumentIds([...originalPaymentIds, selectedPayment?.id])
          : [];

      const deletePromises = idsToDelete.map(id =>
        apiHelper(PaymentService.deleteApiPaymentDueAccount, { showNotification: false }, id)
      );

      const updateCreatePromises = formData.payments.map(async (item) => {
        const paymentObj = createPaymentObject(formData, item, entityId);
        const isExisting = item.id && item.id > 0;

        const apiMethod = isExisting
          ? PaymentService.putApiPaymentDueAccount
          : PaymentService.postApiPaymentDueAccount;

        const payload = isExisting ? { ...paymentObj, id: item.id } : paymentObj;

        const res = await apiHelper(apiMethod, { showNotification: false }, payload);

        // Existing lines already hold the unchanged PDF; re-uploading only stacked duplicates.
        if (res?.data?.success && formData.documentfile && !(documentUnchanged && isExisting)) {
          await uploadDocument(res.data.success.id, formData.documentfile);
        }
      });

      await Promise.all([...deletePromises, ...updateCreatePromises]);

      // Removed or replaced PDF: delete every stored copy, not just the one displayed.
      if (previousDocumentIds.length > 0) {
        try {
          await Promise.all(
            previousDocumentIds.map(docId =>
              apiHelper(DocumentService.deleteApiDocument, { showNotification: false }, docId)
            )
          );
          setOriginalDocumentId(null);
        } catch (deleteErr) {
          console.warn("Failed to delete old document:", deleteErr);
          // Usually safe to continue — don't fail the whole update
        }
      }

      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Payments updated successfully",
        type: NotificationType.Success,
        title: "Payment Updated",
        icon: <img src={GetImages.toastPaymentAccountIcon} alt="success" width="20" height="20" />,
      });

      // Reset form
      reset({
        tenantId: formData.tenantId,
        documentfile: null,
        AccountNumber: "",
        InvoiceNo: "",
        payments: [DEFAULT_PAYMENT_ITEM],
      });

      setResetCounter(prev => prev + 1);
      setOriginalPaymentIds([]);
      fetchData?.();
      navigate(listingPath);
    } catch (error) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to update payment",
        type: NotificationType.Failure,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onCancel = () => navigate(listingPath);
  const addPaymentRow = () => append({ ...DEFAULT_PAYMENT_ITEM });
  const removePaymentRow = (index: number) => remove(index);

  const handleTabChange = (tabId: string) => {
    if (tabId === "dues") fetchTenantDues(tenantId);
    else if (tabId === "transactions") fetchTransactions();
  };

  // Effects
  useEffect(() => {
    fetchTenants();
    fetchPaymentTypes();
  }, []);

  useEffect(() => {
    if (tenantId) {
      fetchTenantDues(tenantId);
      fetchReceivableDetail(tenantId);
    }
  }, [tenantId]);

  useEffect(() => {
    if (urlTenantId) {
      setValue("tenantId", urlTenantId);
    }
  }, [urlTenantId, setValue]);

  // ============================================================================
  // MEMOIZED VALUES
  // ============================================================================

  const tenantOptions = useMemo(() => data.tenants.map(t => ({ Id: t.id, Name: t.name })), [data.tenants]);
  const paymentTypeOptions = useMemo(() => data.paymentTypes.map(t => ({ Id: String(t.id), Name: t.name })), [data.paymentTypes]);

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  const renderBasicDetails = () => (
    <Accordion>
      <AccordionSection title="Basic Details" isOpen={accordionState.basicDetailsOpen} onToggle={() => toggleAccordion('basicDetailsOpen')}>
        <div className="accordion-body">
          <div className="row">
            <div className="col-md-12">
              <form>
                <div className="row align-items-center">
                  <div className="col-md-6 mb-3">
                    <label className="static-field-label">Tenant</label>
                    <div className="static-field-value">
                      {data.selectedReceivableDetail?.payerPayeeName
                        ?? tenantOptions.find((o) => String(o.Id) === String(tenantId))?.Name
                        ?? ""}
                    </div>
                  </div>
                  {tenantId && (
                    <div className="col-md-6 d-none">
                      <label className="fw-600 mt-md-4 w-100 mt-lg-4 mt-0 d-block"> </label>
                      <SimpleOutlineButton
                        style={{ paddingTop: 9, paddingBottom: 9 }}
                        className="w-100 mt-1"
                        type="button"
                        label="View All Dues & Transactions"
                        onClick={() => toggleModal('viewAll')}
                      />
                    </div>
                  )}
                </div>
                <div className="row mt-2">
                  <div className="col-md-4">
                    <InputField
                      name="AccountNumber"
                      label="Account Number"
                      required
                      type="text"
                      placeholder="Enter Account Number"
                      validation={{
                        maxLength: {
                          value: 50,
                          message: "Account number cannot exceed 50 characters"
                        }
                      }}
                    />
                  </div>
                  <div className="col-md-4">
                    <InputField
                      name="InvoiceNo"
                      label="Invoice No./Bill No."
                      type="text"
                      placeholder="Enter Invoice No./Bill No."
                      required
                      requiredMessage="Invoice No./Bill No. is required"
                      validation={{
                        required: "Invoice No./Bill No. is required",
                        maxLength: {
                          value: 100,
                          message: "Invoice number cannot exceed 100 characters"
                        }
                      }}
                    />
                  </div>
                </div>
                <div className="row mt-2">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="fw-600">
                        Upload Bill / Invoice <span className="optionalLabel">(optional)</span>
                      </label>
                      <DocumentUploader name="documentfile" maxSizeMB={5} resetTrigger={resetCounter} />
                    </div>
                  </div>
                </div>
                <div className="row mt-3">
                  <div className="col-12">
                    <SimpleButton type="button" label="Next" onClick={openPaymentInfo} />
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </AccordionSection>
    </Accordion>
  );

  const renderPaymentInfo = () => (
    <Accordion>
      <AccordionSection title="Payment Information" isOpen={accordionState.paymentInfoOpen} onToggle={() => toggleAccordion('paymentInfoOpen')}>


        <div className="accordion-body">
          {fields.map((field, index) => (
            <div className="payment-row" key={field.id}>
              <div className="payment-fields-group">
                <div className="payment-field">
                  <SelectField
                    placeholderText="Select payment type"
                    name={`payments.${index}.PaymentType`}
                    label={index === 0 ? "Payment Type" : ""}
                    required={true}
                    requiredMessage="Payment Type is required"
                    options={paymentTypeOptions}
                    isAsterisk={index === 0}
                  />
                </div>
                <div className="payment-field">
                  <CurrencyInput
                    name={`payments.${index}.Amount`}
                    label={index === 0 ? "Amount" : ""}
                    required={true}
                    requiredMessage="Amount is required"
                    placeholder="Enter Amount"
                    disabled={isLoading}
                  />
                </div>
                <div className="payment-field">
                  <InputField
                    name={`payments.${index}.DueDate`}
                    label={index === 0 ? "Due Date" : ""}
                    type="date"
                    required={true}
                    requiredMessage="Due Date is required"
                    isAsterisk={index === 0}
                  />
                </div>
                <div className="payment-field">
                  <InputField
                    name={`payments.${index}.GracePeriod`}
                    label={index === 0 ? "Grace Period (Days)" : ""}
                    required={true}
                    requiredMessage="Grace Period is required"
                    isAsterisk={index === 0}
                    type="number"
                    placeholder="Enter Grace Period"
                    validation={{
                      min: { value: 0, message: "Grace period cannot be negative" },
                      maxLength: {
                        value: 2,
                        message: "Grace period cannot exceed 2 digits"
                      }
                    }}
                  />
                </div>
                <div className="payment-field description-field">
                  <DescriptionTextarea
                    name={`payments.${index}.Description`}
                    label={index === 0 ? "Description" : ""}
                    placeholder="Enter description"
                    requiredMessage="Description is required"
                    required={true}
                    rows={1}
                    alignCounterWithError
                    validation={{
                      maxLength: {
                        value: 250,
                        message: "Description cannot exceed 250 characters"
                      }
                    }}
                  />
                </div>
              </div>
              <div className="payment-action">
                {/* Matches the real fields' label height -- but ONLY on the row that
                    actually has labels (index 0), since .payment-action has no label
                    of its own and otherwise aligns to the top of the row (the label
                    line) instead of the input line beneath it. */}
                {index === 0 && <div style={{ height: '21px', marginBottom: '0.5rem' }}></div>}

                {fields.length > 1 && (
                  <SvgRoundIconButton
                    style={{ width: 40, background: "#FDE8E8" }}
                    Icon={<DeleteRedIcon fill="#E02424" width="22" height="22" />}
                    onClick={() => removePaymentRow(index)}
                  />
                )}
              </div>
            </div>
          ))}
          <div className="mt-3">
            <SimpleOutlineButton type="button" label="Add More" onClick={addPaymentRow} />
          </div>
        </div>
      </AccordionSection>
    </Accordion>
  );

  const renderTransactionsContent = () => {
    if (isTransactionsLoading) return <div className='loading-card'><SimpleSpinner myClassName='Icon40 mt-5' /></div>;
    if (data.transactions.length === 0) return <EmptyState heading="No Transactions" icon={GetImages.DuePayments} isButton={false} isBorder={false} preserveIconColors />;
    return data.transactions.map(item => <DuesORTransaction key={item.id} item={item} />);
  };


  if (isLoading) {
    return (
      <SimpleSpinner myClassName={"Icon50"} isFullPage={true} />
    );
  }


  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <>
      <div className="d-flex flex-column gap-2 full-page-content edit-payment-container">
        <LeftArrowTopHeader title="Edit Receivable" backPath={listingPath} />
        <ReceivableDetailCard
          Id="ReceivableDetailCard"
          data={data.selectedReceivableDetail}
          loading={isLoadingHeader}
          isBorderRadius={false}
          IsFooter={true}
          isEntity={isEntity}
        entityId={entityId}
        entityTypeId={entityTypeId}
        onDeleteSuccess={() => navigate(listingPath)}

        />
        {!isLoadingHeader && !data.selectedReceivableDetail ? (
          <FormProvider {...methods}>
            <Card className="no-bg p-3 mt-0">
              <div className="row mb-3">
                <div className="col-md-6 mb-3">
                  <label className="static-field-label">Tenant</label>
                  <div className="static-field-value">
                    {data.selectedReceivableDetail?.payerPayeeName
                      ?? tenantOptions.find((o) => String(o.Id) === String(tenantId))?.Name
                      ?? ""}
                  </div>
                </div>
              </div>
              <EmptyState
                heading="No Due Payments"
                icon={GetImages.DuePayments}
                isButton={false}
                isBorder={false}
                preserveIconColors
              />
            </Card>
          </FormProvider>
        ) : null}
        {
          data.selectedReceivableDetail && (
            <>
              <Card className="no-bg p-0 mt-0">
                <div className="edit-payment-content">
                  <div className="row align-items-center">
                    <div className="col-md-4">
                      <PaymentDashboard
                        payments={data.dues}
                        selectedPayment={selectedPayment}
                        onPaymentSelect={handlePaymentSelect}
                      />
                    </div>
                    <div className="col-md-8">

                      <FormProvider {...methods}>
                        <div className="d-flex flex-column gap-3">
                          {isLoading || isDetailLoading ? (
                            <SimpleSpinner myClassName="Icon40 mt-5" />
                          ) : data.dues.length === 0 ? (
                            <EmptyState
                              heading="No Due Payments"
                              icon={GetImages.DuePayments}
                              isButton={false}
                              isBorder={false}
                              preserveIconColors
                            />
                          ) : (
                            <>
                              {renderBasicDetails()}
                              {renderPaymentInfo()}
                            </>
                          )}
                        </div>
                      </FormProvider>


                    </div>
                  </div>
                </div>
              </Card>
              <FixedFooter
                onCancel={onCancel}
                onSubmit={handleSubmit(handlePaymentUpdate)}
                loading={isLoading}
                buttonLabel="Save"
                isSubmitDisabled={isLoading || isDetailLoading || !isDirty || !isValid || data.dues.length === 0}
              />
            </>
          )
        }

      </div>

      <RightSideModal isOpen={modals.viewAll} onClose={() => toggleModal('viewAll')} title="Dues & Transactions" isFooterHidden={true} maxWidth={480}>
        <Card className="p-0 no-bg">
          <DynamicTabs
            tabs={[
              { id: "transactions", label: "Transactions", onClick: handleTabChange, content: renderTransactionsContent() },
            ]}
            defaultActiveTab="dues"
          />
        </Card>
      </RightSideModal>
    </>
  );
};

export default EditReceivable;